import { AnimatePresence } from 'framer-motion';
import orderBy from 'lodash.orderby';
import { FC, memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import useStorageToggle from '@/hooks/useStorageToggle';
import { OptimisationTestUnitResource } from '@/types/resources';
import { OptimisationTestTypeEnum } from '@/utils/enums';
import { OptimisationProjectNetworkEnum } from '@/utils/enums';

import DeleteAllUnitsButton from './DeleteAllUnitsButton';
import EndCompetitionButton from './EndCompetitionButton';
import GoogleAdsExportButton from './GoogleAdsExportButton';
import TestHeading from './TestHeading';
import TestTable from './TestTable';
import TestsStatusHolder from './TestsStatusHolder';
import ThumbsAndTitlesFormModal from './ThumbsAndTitlesFormModal';

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  > button {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: 600;
  }
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
  network: OptimisationProjectNetworkEnum;
  externalProjectId: string;
  projectTitle: string;
  competition?: {
    id: number;
    ended_at?: string;
  };
}

const ThumbsAndTitles: FC<Props> = memo(
  ({ tests, network, externalProjectId, projectTitle, competition }) => {
    const [showTestModal, setShowTestModal] = useState(false);

    const [isOpen, toggleIsOpen] = useStorageToggle(
      `optimisation_test_${externalProjectId}_thumbs`,
    );

    const sortedTests = useMemo(
      () =>
        orderBy(tests, (test) => test.searchStats?.ctrPercentage ?? -1, 'desc'),
      [tests],
    );

    const batches = useMemo(
      () =>
        tests!.reduce<number[]>((acc, curr) => {
          if (acc.includes(curr.batch)) {
            return acc;
          }

          return [...acc, curr.batch];
        }, []),
      [tests],
    );

    return (
      <>
        <TestHeading
          heading="Thumbs"
          description="Finds which thumbnails get the most clicks"
          onToggle={toggleIsOpen}
          isOpen={isOpen}
        />
        {isOpen && (
          <>
            <TestTable
              type={OptimisationTestTypeEnum.THUMBS}
              tests={sortedTests}
              testParameter="ctrPercentage"
              network={network}
            />
            <ButtonWrapper>
              <TestsStatusHolder tests={tests!} />
              <GoogleAdsExportButton
                type={OptimisationTestTypeEnum.THUMBS}
                externalProjectId={externalProjectId}
              />
              <DeleteAllUnitsButton
                type={OptimisationTestTypeEnum.THUMBS}
                batches={batches}
              />
              {!!competition && !competition?.ended_at && (
                <EndCompetitionButton competition={competition} />
              )}
              <Button onClick={() => setShowTestModal(true)} $type="colorized">
                Upload tests
              </Button>
            </ButtonWrapper>
            <AnimatePresence>
              {showTestModal && (
                <ThumbsAndTitlesFormModal
                  projectTitle={projectTitle}
                  onClose={() => setShowTestModal(false)}
                  network={network}
                  competition={competition}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </>
    );
  },
);

export default ThumbsAndTitles;
